import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { filterConsistencyParamDeclarations } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";

import type { DisplayAdvertisingParamsDefinition } from "./displayAdvertisingRoutes.types";

export const displayAdvertisingParamsDefinition: DisplayAdvertisingParamsDefinition = {
    key: {
        type: "string",
        dynamic: false,
    },
    industry: {
        type: "string",
        dynamic: true,
    },
    tab: {
        type: "string",
        dynamic: true,
    },
    webSource: {
        type: "string",
        value: devicesTypes.TOTAL,
    },
    duration: {
        type: "string",
        value: "28d",
        skipUrlParamDefinition: true,
    },
    country: {
        type: "int",
        array: false,
        value: 840,
        skipUrlParamDefinition: true,
    },
    isWWW: {
        type: "string",
        value: "*",
        skipUrlParamDefinition: true,
    },
    sort: {
        type: "string",
        value: null,
        skipUrlParamDefinition: true,
    },
    asc: {
        type: "string",
        value: null,
        skipUrlParamDefinition: true,
    },
    includeTerms: {
        type: "string",
        array: true,
        value: [],
        skipUrlParamDefinition: true,
    },
    adsViewType: {
        type: "int",
        array: false,
        value: 0,
        skipUrlParamDefinition: true,
    },
    pdf: {
        type: "bool",
        array: false,
        value: false,
        skipUrlParamDefinition: true,
    },
    adsSelectedSite: {
        type: "string",
        value: null,
        skipUrlParamDefinition: true,
    },
    campaignsSelectedSite: {
        type: "string",
        value: null,
        skipUrlParamDefinition: true,
    },
    verificationSelectedSite: {
        type: "string",
        value: null,
        skipUrlParamDefinition: true,
    },
    ...filterConsistencyParamDeclarations,
    // filterConsistencyParamDeclarations entries have no skipUrlParamDefinition field —
    // force it on so they never leak into a route's query string (they don't today).
    ignoreFilterConsistency: {
        ...filterConsistencyParamDeclarations.ignoreFilterConsistency,
        skipUrlParamDefinition: true,
    },
    isDurationChanged: {
        ...filterConsistencyParamDeclarations.isDurationChanged,
        skipUrlParamDefinition: true,
    },
    isCountryChanged: {
        ...filterConsistencyParamDeclarations.isCountryChanged,
        skipUrlParamDefinition: true,
    },
    isWebSourceChanged: {
        ...filterConsistencyParamDeclarations.isWebSourceChanged,
        skipUrlParamDefinition: true,
    },
} as const;

const CONTAINER_STATE_NAME = "adintelligence-displayadvertising";

export const ROUTES: Record<
    "container" | "root" | "frame" | "home" | "overview" | "campaigns" | "ads" | "adNetworks" | "publishers",
    { camelCase: string; kebabCase: string; stateName: string; url: string }
> = {
    container: {
        camelCase: "displayAdvertising",
        kebabCase: "display-advertising",
        stateName: CONTAINER_STATE_NAME,
        url: "/displayadvertising",
    },
    // abstract sub-states — no owned url segment, inherit from their parent
    root: {
        camelCase: "displayAdvertisingRoot",
        kebabCase: "display-advertising-root",
        stateName: `${CONTAINER_STATE_NAME}.root`,
        url: "",
    },
    frame: {
        camelCase: "displayAdvertisingFrame",
        kebabCase: "display-advertising-frame",
        stateName: `${CONTAINER_STATE_NAME}.root.frame`,
        url: "",
    },
    home: {
        camelCase: "displayAdvertisingHome",
        kebabCase: "display-advertising-home",
        stateName: `${CONTAINER_STATE_NAME}.root.home`,
        url: "/home__",
    },
    overview: {
        camelCase: "displayAdvertisingOverview",
        kebabCase: "display-advertising-overview",
        stateName: `${CONTAINER_STATE_NAME}.root.frame.overview`,
        url: "/advertiser/overview/:country/:duration",
    },
    campaigns: {
        camelCase: "displayAdvertisingCampaigns",
        kebabCase: "display-advertising-campaigns",
        stateName: `${CONTAINER_STATE_NAME}.root.frame.campaigns`,
        url: "/advertiser/campaigns/:country/:duration",
    },
    ads: {
        camelCase: "displayAdvertisingAds",
        kebabCase: "display-advertising-ads",
        stateName: `${CONTAINER_STATE_NAME}.root.frame.ads`,
        url: "/advertiser/ads/:country/:duration",
    },
    adNetworks: {
        camelCase: "displayAdvertisingAdNetworks",
        kebabCase: "display-advertising-ad-networks",
        stateName: `${CONTAINER_STATE_NAME}.root.frame.adnetworks`,
        url: "/advertiser/adnetworks/:country/:duration",
    },
    publishers: {
        camelCase: "displayAdvertisingPublishers",
        kebabCase: "display-advertising-publishers",
        stateName: `${CONTAINER_STATE_NAME}.root.frame.publishers`,
        url: "/advertiser/publishers/:country/:duration",
    },
} as const;
